import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { Gavel, TrendingUp, Clock, DollarSign, ArrowRight, Loader2 } from "lucide-react";
import { API_URL } from "@/config";
import { Card } from "@/components/ui/card";

interface Listing {
    listing_id: number;
    title: string | null;
    year: number | null;
    make: string | null;
    model: string | null;
    current_bid: string | null;
    buy_now_price: string | null;
    auction_end: string | null;
    status: string;
    auction_mode: string;
    listing_type: string;
    bids: { bid_id: number }[];
}

const STATUS_COLOR: Record<string, string> = {
    ACTIVE:    "bg-green-100 text-green-700",
    DRAFT:     "bg-gray-100 text-gray-600",
    ENDED:     "bg-blue-100 text-blue-700",
    SOLD:      "bg-teal-100 text-teal-700",
    CANCELLED: "bg-red-100 text-red-600",
};

function hoursUntil(end: string | null): string {
    if (!end) return "—";
    const ms = new Date(end).getTime() - Date.now();
    if (ms < 0) return "Ended";
    const hours = Math.floor(ms / 3_600_000);
    if (hours < 24) return `${hours}h left`;
    return `${Math.floor(hours / 24)}d left`;
}

export default function AuctionDashboardPage() {
    const [staff,    setStaff]    = useState<StaffUser | null>(null);
    const [listings, setListings] = useState<Listing[]>([]);
    const [loading,  setLoading]  = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentStaff().then((r) => { if (r.ok && r.staff) setStaff(r.staff); }),
            axios.get(`${API_URL}/auction/listings?status=ACTIVE`, { withCredentials: true })
                .then((r) => setListings(r.data.listings ?? []))
                .catch(() => null),
        ]).finally(() => setLoading(false));
    }, []);

    const now = Date.now();
    const endingSoon = listings.filter((l) => {
        if (!l.auction_end) return false;
        const ms = new Date(l.auction_end).getTime() - now;
        return ms > 0 && ms < 24 * 3_600_000;
    }).length;

    const totalBids = listings.reduce((sum, l) => sum + (l.bids?.length ?? 0), 0);

    const statCards = [
        { label: "Active Listings",  value: listings.length, icon: Gavel,      color: "text-blue-500",   bg: "bg-blue-50"   },
        { label: "Total Bids",       value: totalBids,        icon: TrendingUp, color: "text-violet-500", bg: "bg-violet-50" },
        { label: "Ending in 24h",    value: endingSoon,       icon: Clock,      color: "text-orange-500", bg: "bg-orange-50" },
        { label: "Sold Listings",    value: "—",              icon: DollarSign, color: "text-green-500",  bg: "bg-green-50"  },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">
                    {staff ? `Welcome, ${staff.first_name}` : "Auction Dashboard"}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {staff ? `Auction Staff · ${staff.role.replace(/_/g, " ")}` : "Loading…"}
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            <div className="flex flex-wrap gap-3">
                <Link
                    to="/auctions/marketplace"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <Gavel className="h-4 w-4" /> Marketplace
                </Link>
                <Link
                    to="/auctions/request-account"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <TrendingUp className="h-4 w-4" /> Account Requests
                </Link>
            </div>

            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Active Listings</h2>
                    <Link
                        to="/auctions/marketplace"
                        className="text-xs text-amber-600 hover:underline flex items-center gap-1"
                    >
                        View all <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : listings.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">No active listings</div>
                ) : (
                    <div className="divide-y">
                        {listings.slice(0, 8).map((l) => (
                            <Link
                                key={l.listing_id}
                                to={`/auctions/listings/${l.listing_id}`}
                                className="flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                                        <Gavel className="h-4 w-4 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">
                                            {l.title ?? ([l.year, l.make, l.model].filter(Boolean).join(" ") || `Listing #${l.listing_id}`)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {l.bids?.length ?? 0} bids · Current: {l.current_bid ? `$${Number(l.current_bid).toLocaleString()}` : "—"} · {hoursUntil(l.auction_end)}
                                        </p>
                                    </div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[l.status] ?? "bg-gray-100 text-gray-600"}`}>
                                    {l.auction_mode}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
